import React, { useEffect } from "react";
import * as R from "remeda";
import { useStore } from "zustand";

import {
    useCheckDimensionStatus,
    useIsAnyDimensionIdLoading,
} from "../hooks/dimension/dimension.hooks";
import { zustandStore } from "../store/zustand.helpers";
import type { DimensionTemplates } from "../types/dimensions";

import { DimensionStatus, dimensionStatusStore } from "./dimension.status";

export enum HydrationState {
    PENDING = "pending",
    LOADING = "loading",
    SUCCESS = "success",
    ERROR = "error",
}

export interface DimensionHydrationState {
    dimensionHydrationStatus: Record<string, HydrationState>;
    errorMessages: Record<string, string>;
    payload: any;
    currentDimensions: { [key in string]: DimensionTemplates };
}

export interface DimensionHydrationActions {
    initHydration: (dimensions: string[], hydrationPayload: any) => void;
    setHydrationState: (dimension: string, hydration: HydrationState) => void;
    setHydrationErrorMessage: (dimension: string, errorMessage: string) => void;
    doesDimensionRequireHydration: (dimension: string) => boolean;
    isHydrationDone: () => boolean;
    getHydrationPayload: () => any;
    isDependenciesNotPending: (dependencies: string[]) => boolean;
    isDependenciesAllSuccess: (dependencies: string[]) => boolean;
    clearAllState: () => void;
    setCurrentDimensions: (dimensions: DimensionTemplates[]) => void;
}

const initialState: DimensionHydrationState = {
    payload: undefined,
    dimensionHydrationStatus: {},
    errorMessages: {},
    currentDimensions: {},
};

export const { store: dimensionHydrationStore } = zustandStore<
    DimensionHydrationState,
    DimensionHydrationActions
>("Dimension Hydration", initialState, (set, get) => ({
    initHydration: (dimensions: string[], hydrationPayload: any) => {
        const initialHydrationState = R.pipe(
            dimensions,
            R.map((e) => [e, HydrationState.PENDING] as const),
            R.fromPairs<HydrationState>,
        );
        set(() => ({
            dimensionHydrationStatus: initialHydrationState,
            payload: hydrationPayload,
        }));
    },
    setHydrationState(dimension, hydration) {
        set(() => ({
            dimensionHydrationStatus: {
                ...get().dimensionHydrationStatus,
                [dimension]: hydration,
            },
        }));
    },
    doesDimensionRequireHydration: (dimensionId: string) => {
        const requestHydration = get().dimensionHydrationStatus;
        return (
            requestHydration &&
            requestHydration[dimensionId] &&
            requestHydration[dimensionId] === HydrationState.PENDING
        );
    },
    isDependenciesNotPending(dependencies) {
        return (
            R.pipe(
                get().dimensionHydrationStatus,
                R.pick(dependencies),
                R.values,
                R.filter((e) => e === HydrationState.PENDING),
                R.length(),
            ) === 0
        );
    },

    isDependenciesAllSuccess(dependencies) {
        return (
            R.pipe(
                get().dimensionHydrationStatus,
                R.pick(dependencies),
                R.values,
                R.filter((e) => e === HydrationState.SUCCESS),
                R.length(),
            ) === dependencies.length
        );
    },
    getHydrationPayload() {
        return get().payload;
    },
    isHydrationDone() {
        return (
            Object.values(get().dimensionHydrationStatus).find(
                (e) => e === HydrationState.PENDING,
            ) === undefined
        );
    },
    setHydrationErrorMessage(dimension, errorMessage) {
        set(() => ({
            dimensionHydrationStatus: {
                ...get().dimensionHydrationStatus,
                [dimension]: HydrationState.ERROR,
            },
            errorMessages: {
                ...get().errorMessages,
                [dimension]: errorMessage,
            },
        }));
    },
    clearAllState() {
        set(() => ({
            dimensionHydrationStatus: {},
            errorMessages: {},
        }));
    },
    setCurrentDimensions(dimensions: DimensionTemplates[]) {
        const currentDimensions: { [key in string]: DimensionTemplates } = {};
        for (const dimension of dimensions) {
            currentDimensions[dimension.id] = dimension;
        }
        set(() => ({
            currentDimensions,
        }));
    },
}));

export const useDimensionHydration = (
    dimensionId: string,
    dependencies: string[],
    hydrationCallback: (hydrationPayload: object) => Promise<void>,
    hydrationErrorEvent?: () => void,
) => {
    const [
        requiresHydration,
        hydrationPayload,
        setHydrationState,
        setHydrationErrorMessage,
        hydrationDependenciesReady,
        hydrationDependenciesAllSuccess,
        currentDimensions,
    ] = useStore(dimensionHydrationStore, (state) => [
        state.doesDimensionRequireHydration(dimensionId),
        state.getHydrationPayload(),
        state.setHydrationState,
        state.setHydrationErrorMessage,
        state.isDependenciesNotPending(dependencies),
        state.isDependenciesAllSuccess(dependencies),
        state.currentDimensions,
    ]);

    const [setDimensionStatus] = useStore(dimensionStatusStore, (state) => [
        state.setStatus,
    ]);

    const dependenciesStatusAllSuccess = useCheckDimensionStatus(dependencies);
    const dependenciesLoading = useIsAnyDimensionIdLoading(dependencies);

    const readyForHydration = React.useMemo(
        () =>
            requiresHydration &&
            hydrationDependenciesReady &&
            !dependenciesLoading,
        [requiresHydration, hydrationDependenciesReady, dependenciesLoading],
    );

    useEffect(() => {
        if (readyForHydration) {
            if (
                hydrationDependenciesAllSuccess &&
                dependenciesStatusAllSuccess
            ) {
                try {
                    hydrationCallback(hydrationPayload)
                        .then(() => {
                            setHydrationState(
                                dimensionId,
                                HydrationState.SUCCESS,
                            );
                            setDimensionStatus(
                                dimensionId,
                                DimensionStatus.SUCCESS,
                            );
                        })
                        .catch((e) => {
                            if (hydrationErrorEvent) hydrationErrorEvent();
                            setHydrationErrorMessage(
                                dimensionId,
                                (e as Error).message,
                            );
                            setDimensionStatus(
                                dimensionId,
                                DimensionStatus.EMPTY,
                            );
                        });
                } catch (e) {
                    setDimensionStatus(dimensionId, DimensionStatus.EMPTY);
                    setHydrationErrorMessage(dimensionId, (e as Error).message);
                    if (hydrationErrorEvent) hydrationErrorEvent();
                }
            } else {
                if (currentDimensions[dimensionId]?.optional) {
                    setHydrationState(dimensionId, HydrationState.SUCCESS);
                    setDimensionStatus(dimensionId, DimensionStatus.SUCCESS);
                    return;
                }
                setDimensionStatus(dimensionId, DimensionStatus.EMPTY);
                setHydrationErrorMessage(
                    dimensionId,
                    "dependencies failed to hydrate",
                );
            }
        }
    }, [
        readyForHydration,
        hydrationDependenciesAllSuccess,
        dependenciesStatusAllSuccess,
    ]);
};
